import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Encapsulates dataZoom state machine:
 *  - lastZoom ref (preserves zoom across legend toggles, no rerender)
 *  - zoomRange state (drives rerender for 2nd x-axis label density)
 *  - debounce timer (avoids flooding React on drag)
 *  - skipNext flag (ignores programmatic datazoom events on data change)
 *
 * Resets zoom whenever `data` reference or `brushIndex` endpoints change.
 */
export function useZoomState(
  data: any[],
  brushStartIndex?: number,
  brushEndIndex?: number,
  xDataKey?: string,
) {
  const lastZoom = useRef<{ start: number; end: number } | null>(null);
  const [zoomRange, setZoomRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const zoomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipNextZoomEventRef = useRef<boolean>(false);
  const prevDataRef = useRef<any[]>([]);
  const prevBrushStartRef = useRef<number | undefined>(undefined);
  const prevBrushEndRef = useRef<number | undefined>(undefined);

  const hasXAxisChanged = useCallback(
    (nextData: any[], prevData: any[]) => {
      if (nextData.length !== prevData.length) return true;
      if (!xDataKey) return nextData !== prevData;
      for (let i = 0; i < nextData.length; i += 1) {
        if (nextData[i]?.[xDataKey] !== prevData[i]?.[xDataKey]) {
          return true;
        }
      }
      return false;
    },
    [xDataKey],
  );

  useEffect(() => {
    const xAxisChanged = hasXAxisChanged(data, prevDataRef.current);
    const brushRangeChanged =
      brushStartIndex !== prevBrushStartRef.current ||
      brushEndIndex !== prevBrushEndRef.current;

    if (xAxisChanged || brushRangeChanged) {
      lastZoom.current = null;
      if (zoomDebounceRef.current) {
        clearTimeout(zoomDebounceRef.current);
        zoomDebounceRef.current = null;
      }
      skipNextZoomEventRef.current = true;
      setZoomRange(null);
    }

    prevDataRef.current = data;
    prevBrushStartRef.current = brushStartIndex;
    prevBrushEndRef.current = brushEndIndex;
  }, [data, brushStartIndex, brushEndIndex, hasXAxisChanged]);

  useEffect(() => {
    const ref = zoomDebounceRef;
    return () => {
      if (ref.current) clearTimeout(ref.current);
    };
  }, []);

  const handleDataZoom = useCallback(
    (start: number, end: number) => {
      lastZoom.current = { start, end };
      if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
      zoomDebounceRef.current = setTimeout(() => {
        setZoomRange({ start, end });
      }, 150);
    },
    [],
  );

  const consumeSkipFlag = useCallback(() => {
    if (skipNextZoomEventRef.current) {
      skipNextZoomEventRef.current = false;
      return true;
    }
    return false;
  }, []);

  return {
    lastZoom,
    zoomRange,
    handleDataZoom,
    consumeSkipFlag,
  };
}
